import Fieldset from 'components/_UI/Fieldset'
import InfoTooltip from 'components/_UI/InfoTooltip'
import Switch from 'components/_UI/Switch'
import currency from 'helpers/currency'
import fieldValidations from 'helpers/fieldValidations'
import React, { useState } from 'react'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'
import styled from 'styled-components'
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import colors from 'theme/colors'
import { v4 as uuidv4 } from 'uuid';
import SalePeriodForm from './SalePeriodForm'
import ConfirmDeleteModal from './ConfirmDeleteModal'

export default function FormBatches({
    batches,
    setTicket,
    ticketIndex,
    hasSalePeriod,
    setDeletedBatches = () => false
}) {
    const [batchErrors, setBatchErrors] = useState({})
    const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(null)

    function setBatchError(index, field, message) {
        setBatchErrors(prev => ({
            ...prev,
            [`${index}_${field}`]: message
        }))
    }

    function getBatchError(index, field) {
        return batchErrors[`${index}_${field}`] || ''
    }

    function reindexErrors(removedIndex) {
        setBatchErrors(prev => {
            const newErrors = {}
            for (const key of Object.keys(prev)) {
                const [idxStr, field] = key.split('_')
                const idx = Number(idxStr)
                if (idx === removedIndex) continue
                const newIdx = idx > removedIndex ? idx - 1 : idx
                newErrors[`${newIdx}_${field}`] = prev[key]
            }
            return newErrors
        })
    }

    function calculateFee(value) {
        return value > 25 ? value * 0.1 : 2.5
    }

    function calculateNetIncome(value) {
        const fee = calculateFee(value)
        return (value - fee) > 0
            ? currency.format(value - fee)
            : currency.format(0)
    }

    const addBatch = () => {
        setTicket(tickets => {
            const newTickets = JSON.parse(JSON.stringify(tickets))
            newTickets[ticketIndex].batches.push({
                internal_id: uuidv4(),
                price: '',
                floatPrice: 0,
                quantity: '',
                description: '',
                is_active: false
            })

            return newTickets
        })
    };

    const updateBatch = (key, value, index) => {
        const batch = {
            ...batches[index],
            [key]: value
        }
        setTicket(tickets => {
            const newTickets = JSON.parse(JSON.stringify(tickets))
            newTickets[ticketIndex].batches[index] = batch

            return newTickets
        })
    };

    const removeBatch = (index) => {
        setTicket(tickets => {
            const newTickets = JSON.parse(JSON.stringify(tickets))
            const removed = newTickets[ticketIndex].batches.splice(index, 1)

            if (removed[0]?.id) {
                setDeletedBatches(removedBatches => {
                    return [
                        ...removedBatches,
                        removed[0].id
                    ]
                })
            }

            return newTickets
        })
        reindexErrors(index)
        setBatchDeleteConfirm(null)
    };

    const handlePriceChange = (value, name, values, index) => {
        const batch = {
            ...batches[index],
            price: value,
            floatPrice: values.float
        }

        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].batches[index] = batch

            return newTickets
        })

        if (!value) {
            setBatchError(index, 'price', 'O preço do ingresso é obrigatório')
            return
        }

        if (values.float < 5) {
            setBatchError(index, 'price', 'O preço deve ser no mínimo R$5,00')
            return
        }

        setBatchError(index, 'price', '')
    };

    const handleQuantityChange = (value, index) => {
        const parsedValue = !value ? '' : value.replace(/\D/g, '');
        updateBatch('quantity', parsedValue, index);

        if (!parsedValue) {
            setBatchError(index, 'quantity', 'A quantidade é obrigatória')
            return
        }

        if (Number(parsedValue) <= 0) {
            setBatchError(index, 'quantity', 'A quantidade deve ser acima de 0')
            return
        }

        setBatchError(index, 'quantity', '')
    };

    const handleSalePeriodChange = (salePeriod, index) => {
        const { sale_start, sale_end } = salePeriod
        const batch = {
            ...batches[index],
            sale_start,
            sale_end
        }

        setTicket(tickets => {
            const newTickets = [...tickets]
            newTickets[ticketIndex].batches[index] = batch

            return newTickets
        })
    }

    const createSalePeriod = (batch) => {
        return {
            sale_start: batch.sale_start,
            sale_end: batch.sale_end
        }
    }

    return (
        <Container>
            <BatchesScroll>
                {batches?.map((batch, index) => (
                    <BatchCard key={batch.internal_id}>
                        <BatchHeader>
                            <BatchTitle>Lote {index + 2}</BatchTitle>
                            <RemoveButton onClick={() => setBatchDeleteConfirm(index)}>
                                <Trash2 size={15} />
                            </RemoveButton>
                        </BatchHeader>

                        <ModernFormArea>
                            <FieldWrapper>
                                <Fieldset
                                    layout='squired'
                                    label='Nome'
                                    placeholder='Nome'
                                    value={batch.name || `Lote ${index + 2}`}
                                    onChangeValue={e => updateBatch('name', e, index)}
                                    validations={fieldValidations.description('Nome')}
                                />
                            </FieldWrapper>

                            <FieldRow>
                                <FieldWrapper>
                                    <CurrencyInputField
                                        layout='squired'
                                        label='Valor'
                                        placeholder='Valor'
                                        value={batch.price}
                                        syncProp={true}
                                        onChange={(value, name, values) => handlePriceChange(value, name, values, index)}
                                    />
                                    {getBatchError(index, 'price') && (
                                        <FieldError>
                                            <AlertCircle size={12} />
                                            {getBatchError(index, 'price')}
                                        </FieldError>
                                    )}
                                </FieldWrapper>

                                <FieldWrapper>
                                    <Fieldset
                                        layout='squired'
                                        label='Quantidade'
                                        placeholder='Quantidade'
                                        value={batch.quantity}
                                        syncProp={true}
                                        onChangeValue={e => handleQuantityChange(e, index)}
                                    />
                                    {getBatchError(index, 'quantity') && (
                                        <FieldError>
                                            <AlertCircle size={12} />
                                            {getBatchError(index, 'quantity')}
                                        </FieldError>
                                    )}
                                </FieldWrapper>
                            </FieldRow>

                            <FieldWrapper>
                                <Fieldset
                                    layout='squired'
                                    label='Descrição'
                                    placeholder='Descrição'
                                    value={batch.description}
                                    onChangeValue={e => updateBatch('description', e, index)}
                                    validations={fieldValidations.description('Descrição')}
                                />
                            </FieldWrapper>
                        </ModernFormArea>

                        {hasSalePeriod &&
                            <SalePeriodSection>
                                <SalePeriodForm
                                    salePeriod={createSalePeriod(batch)}
                                    onChangeSalePeriod={(salePeriod) => handleSalePeriodChange(salePeriod, index)}
                                />
                            </SalePeriodSection>
                        }

                        <SwitchRow>
                            <SwitchControl>
                                <Switch
                                    checked={batch.is_active}
                                    onChange={e => updateBatch('is_active', e, index)}
                                />
                            </SwitchControl>
                            <SwitchLabel>
                                Lote ativo
                                <InfoTooltip
                                    bgColor={colors.occasionalPurple}
                                    text="Se ativo, os ingressos deste lote ficaram disponiveis para compra."
                                />
                            </SwitchLabel>
                        </SwitchRow>

                        <FinancialSummary>
                            <SummaryRow>
                                <SummaryLabel>Preço</SummaryLabel>
                                <SummaryValue>{currency.format(batch.floatPrice)}</SummaryValue>
                            </SummaryRow>
                            <SummaryRow>
                                <SummaryLabel>Taxa</SummaryLabel>
                                <SummaryValue>{currency.format(calculateFee(batch.floatPrice))}</SummaryValue>
                            </SummaryRow>
                            <SummaryDivider />
                            <SummaryRow>
                                <SummaryLabel bold>Recebe</SummaryLabel>
                                <SummaryValue bold positive>
                                    {calculateNetIncome(batch.floatPrice)}
                                </SummaryValue>
                            </SummaryRow>
                        </FinancialSummary>
                    </BatchCard>
                ))}

                <AddBatchCard onClick={addBatch}>
                    <AddBatchIcon>
                        <Plus size={20} />
                    </AddBatchIcon>
                    <AddBatchLabel>Novo lote</AddBatchLabel>
                </AddBatchCard>
            </BatchesScroll>

            <ConfirmDeleteModal
                isOpen={batchDeleteConfirm !== null}
                title="Excluir lote"
                message="Tem certeza que deseja excluir este lote? Esta ação não pode ser desfeita."
                onConfirm={() => removeBatch(batchDeleteConfirm)}
                onCancel={() => setBatchDeleteConfirm(null)}
            />
        </Container>
    );
}

const Container = styled.div`
    width: 100%;
    margin-top: 8px;
`;

const BatchesScroll = styled.div`
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: ${colors.night}22;
        border-radius: 4px;
    }
`;

const BatchCard = styled.div`
    min-width: 280px;
    max-width: 300px;
    border: 1.5px solid ${colors.night}18;
    border-radius: 10px;
    padding: 14px;
    background: ${colors.night}04;
    flex-shrink: 0;
    transition: border-color 0.15s ease;

    &:hover {
        border-color: ${colors.night}33;
    }
`;

const BatchHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const BatchTitle = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.secondaryPurple};
`;

const RemoveButton = styled.span`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${colors.danger};
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.danger}10;
    }
`;

const ModernFormArea = styled.div`
    input:not([class*="css-"]):not([id*="react-select"]) {
        background: #fff !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 8px !important;
        padding: 10px 12px !important;
        font-size: 13px !important;
        color: ${colors.nightDark} !important;
        transition: all 0.15s ease !important;

        &::placeholder {
            color: ${colors.night}88 !important;
        }

        &:focus {
            border-color: ${colors.occasionalPurple} !important;
            box-shadow: 0 0 0 3px ${colors.occasionalPurple}18 !important;
        }
    }

    label {
        font-size: 12px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
    }
`;

const FieldWrapper = styled.div`
    width: 100%;
    margin-bottom: 8px;
`;

const FieldRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 4px;
    padding: 3px 6px;
    background: ${colors.danger}0A;
    border-radius: 4px;
`;

const SalePeriodSection = styled.div`
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${colors.night}11;
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid ${colors.night}11;
`;

const SwitchControl = styled.div`
    flex-shrink: 0;
    width: auto;

    & > div {
        width: auto;
    }
`;

const SwitchLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.nightDark};
`;

const FinancialSummary = styled.div`
    margin-top: 10px;
    padding: 10px;
    background: ${colors.night}08;
    border-radius: 8px;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
`;

const SummaryLabel = styled.span`
    font-size: 12px;
    color: ${colors.night};
    ${({ bold }) => bold && `
        font-weight: 700;
        color: ${colors.nightDark};
    `};
`;

const SummaryValue = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ positive }) => positive ? colors.success : colors.night};
    ${({ bold }) => bold && `
        font-size: 13px;
        font-weight: 700;
    `};
`;

const SummaryDivider = styled.div`
    height: 1px;
    background: ${colors.night}15;
    margin: 4px 0;
`;

const AddBatchCard = styled.div`
    min-width: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${colors.occasionalPurple}33;
    border-radius: 10px;
    padding: 20px;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${colors.occasionalPurple}66;
        background: ${colors.occasionalPurple}05;
    }
`;

const AddBatchIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${colors.occasionalPurple}12;
    color: ${colors.occasionalPurple};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
`;

const AddBatchLabel = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;
